import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, Checkbox } from '@feathr/components';

import * as styles from './GoalDrawerEditWarning.css';

export interface IGoalDrawerEditWarningProps {
  acknowledged: boolean;
  activeCampaignsCount: number;
  onAcknowledge: (value: boolean) => void;
  onCreateGoal?: () => void;
  totalConversions: number;
}

function GoalDrawerEditWarning({
  acknowledged,
  activeCampaignsCount,
  onAcknowledge,
  onCreateGoal,
  totalConversions,
}: Readonly<IGoalDrawerEditWarningProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  function handleAcknowledge(newValue?: boolean): void {
    onAcknowledge(!!newValue);
  }

  return (
    <Stack gap={theme.spacing['4']}>
      <Alert
        description={t(
          "It's tracked on {{activeCampaignsCount}} active campaigns and has {{totalConversions}} logged conversions. Historical data will be preserved; future conversions use the new definition. Need a campaign-specific variant?",
          { activeCampaignsCount, totalConversions },
        )}
        title={t('Editing changes this goal everywhere.')}
        type={'warning'}
      >
        {onCreateGoal && (
          <Button
            className={styles.createGoal}
            name={'create-goal'}
            onClick={onCreateGoal}
            type={'link'}
          >
            {t('Create a new goal instead →')}
          </Button>
        )}
      </Alert>
      <Checkbox
        label={t('I understand this change applies account-wide.')}
        layout={'well'}
        onChange={handleAcknowledge}
        value={acknowledged}
      />
    </Stack>
  );
}

export default GoalDrawerEditWarning;
